/**
 * Installations service
 */

import { apiClient } from '../utils/api';
import type { Installation, ApiResponse, InstallationType, InstallationStatus } from '../../../shared/src/types';

export interface ListInstallationsParams {
  status?: InstallationStatus;
  type?: InstallationType;
  limit?: number;
  offset?: number;
}

export async function listInstallations(params?: ListInstallationsParams): Promise<Installation[]> {
  const queryParams = new URLSearchParams();
  if (params?.status) queryParams.append('status', params.status);
  if (params?.type) queryParams.append('type', params.type);
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.offset) queryParams.append('offset', params.offset.toString());

  const url = `/installations${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const response = await apiClient.get<ApiResponse<Installation[]>>(url);

  if (response.data.error) {
    throw new Error(response.data.error.message || 'Error fetching installations');
  }

  return response.data.data || [];
}

export async function getInstallation(id: string): Promise<Installation> {
  const response = await apiClient.get<ApiResponse<Installation>>(`/installations/${id}`);

  if (response.data.error) {
    throw new Error(response.data.error.message || 'Error fetching installation');
  }

  if (!response.data.data) {
    throw new Error('Installation not found');
  }

  return response.data.data;
}

export interface CreateInstallationData {
  name: string;
  type: InstallationType;
  address?: string;
  metadata?: Record<string, any>;
}

export async function createInstallation(data: CreateInstallationData): Promise<Installation> {
  const response = await apiClient.post<ApiResponse<Installation>>('/installations', data);

  if (response.data.error) {
    throw new Error(response.data.error.message || 'Error creating installation');
  }

  if (!response.data.data) {
    throw new Error('Invalid response from server');
  }

  return response.data.data;
}

export interface UpdateInstallationData {
  name?: string;
  type?: InstallationType;
  address?: string;
  status?: InstallationStatus;
  metadata?: Record<string, any>;
}

export async function updateInstallation(
  id: string,
  data: UpdateInstallationData
): Promise<Installation> {
  const response = await apiClient.put<ApiResponse<Installation>>(`/installations/${id}`, data);

  if (response.data.error) {
    throw new Error(response.data.error.message || 'Error updating installation');
  }

  if (!response.data.data) {
    throw new Error('Invalid response from server');
  }

  return response.data.data;
}

export async function deleteInstallation(id: string): Promise<void> {
  const response = await apiClient.delete<ApiResponse<{ message: string }>>(`/installations/${id}`);

  if (response.data.error) {
    throw new Error(response.data.error.message || 'Error deleting installation');
  }
}

